import { useCallback } from "react";
import { addAdToSupabase, updateAdInSupabase, deleteAdFromSupabase } from "../data/supabaseAdsCrud";
import { toast } from "sonner";
import { Ad, AdData } from "@/models/ad";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";

export const useAdsCrud = (
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>,
  refreshData: () => Promise<void>
) => {
  const { isAdmin } = useAuth();
  
  const addAd = useCallback(
    async (adData: AdData) => {
      setIsLoading(true);
      try {
        console.log("Agregando anuncio con pantallas:", adData.screens_json?.map(s => s.screenId));
        console.log("Material URL:", adData.material_url);
        
        // Ensure duration is always a number and add isExpired field
        const dataWithRequiredFields = {
          ...adData,
          duration: Number(adData.duration) || 0,
          material_url: adData.material_url || "",
          is_expired: false
        };
        
        const newAd = await addAdToSupabase(dataWithRequiredFields);
        
        toast.success("Anuncio agregado con éxito");
        await refreshData();
        return newAd;
      } catch (error) {
        console.error("Error al agregar anuncio:", error);
        toast.error("Error al agregar anuncio");
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [refreshData, setIsLoading]
  );

  const updateAd = useCallback(
    async (ad: Ad): Promise<boolean> => {
      setIsLoading(true);
      try {
        await updateAdInSupabase(ad);
        toast.success("Anuncio actualizado con éxito");
        await refreshData();
        return true;
      } catch (error) {
        console.error("Error al actualizar anuncio:", error);
        toast.error("Error al actualizar anuncio");
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [refreshData, setIsLoading]
  );

  const deleteAd = useCallback(
    async (id: string): Promise<boolean> => {
      if (!isAdmin) {
        toast.error("No tienes permisos para eliminar anuncios");
        return false;
      }

      setIsLoading(true);
      try {
        console.log("Iniciando eliminación del anuncio:", id);
        
        // Primero intentamos eliminar de Supabase
        const result = await deleteAdFromSupabase(id);
        
        if (!result) {
          console.error("Error al eliminar anuncio de Supabase");
          toast.error("Error al eliminar anuncio");
          return false;
        }

        console.log("Anuncio eliminado exitosamente de Supabase");
        toast.success("Anuncio eliminado con éxito");
        
        // Actualizamos los datos locales
        await refreshData();
        return true;
      } catch (error) {
        console.error("Error inesperado al eliminar anuncio:", error);
        toast.error("Error inesperado al eliminar anuncio");
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [isAdmin, refreshData, setIsLoading]
  );

  return {
    addAd,
    updateAd,
    deleteAd
  };
};
